import { useState, useEffect } from 'react';

// Redux
import { useHistory, useParams } from 'react-router';
import { FlowsQuestion } from '../types/FlowsTypes';
import {
  popSubView,
  pushSubView,
  resetSubView,
} from '../store/redux/slices/flowsSlice';
import { FlowRouteSubViewParams } from '../types/RouteParams';
import { useFormContext } from 'react-hook-form';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../common/src/utils/globalVariables';
import { isEmpty, omit } from 'lodash';
import useAnswerForQuestion from './useAnswerForQuestion';

interface UseSubView {
  currentSubView: string;
  nextSubView: (arg0: string) => void;
  previousSubView: () => void;
  changeSubView: (arg0: string) => void;
  hasAnswer: boolean;
}

interface Props {
  question: FlowsQuestion;
  defaultView: string;
  defaultViewForDefinedAnswer?: string;
}

const useSubView = ({
  question,
  defaultView: defaultViewProp,
  defaultViewForDefinedAnswer,
}: Props): UseSubView => {
  const dispatch = useFlowsDispatch();
  const history = useHistory();
  const answers = useFlowsSelector((state) => state.flows.answers);
  const getAnswerForQuestion = useAnswerForQuestion();
  const currentAnswer = getAnswerForQuestion(question.id, question.index);

  const previousHistory = currentAnswer?.subViewHistory?.previous;
  const nextHistory = currentAnswer?.subViewHistory?.next;
  const { flowId, sessionId, position, subView } =
    useParams<FlowRouteSubViewParams>();
  const { clearErrors } = useFormContext();

  const hasAnswer = !isEmpty(
    omit(currentAnswer?.answer as Record<string, unknown>, '_local'),
  );

  /**
   * If the answer is already defined we do not need some views
   */
  const getDefaultView = () => {
    if (defaultViewForDefinedAnswer == null) return defaultViewProp;
    if (hasAnswer) return defaultViewForDefinedAnswer;
    return defaultViewProp;
  };

  const defaultView = getDefaultView();

  const [currentSubView, setCurrentSubView] = useState<string>(
    previousHistory?.[previousHistory?.length - 1] || defaultView,
  );

  useEffect(() => {
    if (currentSubView) {
      const newRoute = `/flows/${flowId}/questions/${sessionId}/${position}/${currentSubView}`;
      if (!subView) {
        history.replace(newRoute);
      } else {
        history.push(newRoute);
      }
    }
  }, [currentSubView]);

  useEffect(() => {
    if (subView) setCurrentSubView(subView);
  }, [subView]);

  useEffect(() => {
    setCurrentSubView(
      previousHistory?.[previousHistory?.length - 1] || defaultView,
    );
  }, [answers]);

  const nextSubView = (subView: string) => {
    dispatch(
      pushSubView({
        id: question.id,
        subView,
        position: parseInt(position),
        index: question.index,
        stack: 'previous',
      }),
    );
    dispatch(
      popSubView({ id: question.id, index: question.index, stack: 'next' }),
    );
  };

  const previousSubView = () => {
    clearErrors();
    dispatch(
      pushSubView({
        id: question.id,
        subView,
        position: parseInt(position),
        index: question.index,
        stack: 'next',
      }),
    );
    dispatch(
      popSubView({ id: question.id, index: question.index, stack: 'previous' }),
    );
  };

  const changeSubView = (subView: string) => {
    dispatch(
      resetSubView({
        id: question.id,
        index: question.index,
        subView,
      }),
    );
  };

  useEffect(() => {
    nextSubView(defaultView); // Manually add default view to previous stack
  }, [defaultView]);

  const handleBrowserNavigation = (event: PopStateEvent) => {
    const eventTarget = event.target as Window;
    const { location: targetLocation } = eventTarget;

    const locationParts = targetLocation.hash.split('/');
    const subView = locationParts[locationParts.length - 1];
    if (!RegExp(/^[a-zA-Z]+$/).test(subView)) return; // Only allow letters (test if last route part is subView)

    if (
      previousHistory?.includes(subView) &&
      previousHistory.findIndex(
        (subViewSearch) => subViewSearch === subView,
      ) !==
        previousHistory.length - 1
    ) {
      return previousSubView();
    }
    if (nextHistory?.includes(subView)) {
      return nextSubView(subView);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', handleBrowserNavigation);
    return () => {
      window.removeEventListener('popstate', handleBrowserNavigation);
    };
  }, [nextHistory, previousHistory]);

  return {
    currentSubView,
    nextSubView,
    previousSubView,
    changeSubView,
    hasAnswer,
  };
};

export default useSubView;
